import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ExternalLink, LoaderCircle, Play, VideoOff } from 'lucide-react';

import { HighlightedSelections } from '@/components/HighlightedSelections';
import { Button } from '@/components/ui/button';
import type { YoutubeCaption, YoutubeHighlightSpan } from '@/services';
import { cn } from '@/services/utils';

interface ActiveCue {
  sentenceId: number;
  text: string;
  startChar: number;
  startMilliseconds: number;
  endMilliseconds: number;
}

const PLAYER_POLL_INTERVAL_MILLISECONDS = 250;
const YOUTUBE_API_LOAD_TIMEOUT_MILLISECONDS = 10_000;
const PLAYER_READY_TIMEOUT_MILLISECONDS = 10_000;

interface YoutubePlayer {
  destroy(): void;
  getCurrentTime(): number;
  loadVideoById(options: { videoId: string; startSeconds: number }): void;
  pauseVideo(): void;
  playVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  /** Undocumented but long-stable; the only way to override the viewer's account-level caption preference. */
  unloadModule?(module: string): void;
}

interface YoutubePlayerEvent {
  target: YoutubePlayer;
}

interface YoutubePlayerOptions {
  events: {
    onAutoplayBlocked: () => void;
    onError: (event: { data: number }) => void;
    onReady: (event: YoutubePlayerEvent) => void;
    onStateChange: (event: { data: number }) => void;
  };
}

// https://developers.google.com/youtube/iframe_api_reference#onStateChange
const PLAYER_STATE_PLAYING = 1;
const PLAYER_STATE_BUFFERING = 3;

interface YoutubeIframeApi {
  Player: new (element: HTMLElement, options: YoutubePlayerOptions) => YoutubePlayer;
}

declare global {
  interface Window {
    YT?: YoutubeIframeApi;
    onYouTubeIframeAPIReady?: () => void;
  }
}

/**
 * Each video load re-initialises YouTube's captions from the viewer's account preference,
 * so this runs again whenever playback starts. Failure must never block playback.
 */
const hideYoutubeCaptions = (player: YoutubePlayer) => {
  try {
    player.unloadModule?.('captions');
  } catch (error) {
    console.warn('Unable to hide the built-in YouTube captions.', error);
  }
};

let youtubeIframeApiPromise: Promise<YoutubeIframeApi> | null = null;

const loadYoutubeIframeApi = (): Promise<YoutubeIframeApi> => {
  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }

  if (youtubeIframeApiPromise) {
    return youtubeIframeApiPromise;
  }

  const pendingPromise = new Promise<YoutubeIframeApi>((resolve, reject) => {
    let settled = false;
    let script: HTMLScriptElement;
    let timeoutId: number | undefined;
    const previousReady = window.onYouTubeIframeAPIReady;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      if (window.onYouTubeIframeAPIReady === handleReady) {
        window.onYouTubeIframeAPIReady = previousReady;
      }
    };

    const succeed = (api: YoutubeIframeApi) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(api);
    };

    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const handleReady = () => {
      try {
        previousReady?.();
      } catch (error) {
        console.error('An existing YouTube API ready callback failed.', error);
      }

      if (window.YT?.Player) {
        succeed(window.YT);
      } else {
        fail(new Error('The YouTube player API did not initialize.'));
      }
    };

    const handleLoad = () => {
      if (window.YT?.Player) {
        succeed(window.YT);
      }
    };

    const handleError = () => {
      fail(new Error('Unable to load the YouTube player API.'));
    };

    window.onYouTubeIframeAPIReady = handleReady;

    script = document.querySelector<HTMLScriptElement>(
      'script[src="https://www.youtube.com/iframe_api"]'
    ) ?? document.createElement('script');

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    timeoutId = window.setTimeout(() => {
      fail(new Error('Timed out while loading the YouTube player API.'));
    }, YOUTUBE_API_LOAD_TIMEOUT_MILLISECONDS);

    if (!script.isConnected) {
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.head.appendChild(script);
    }
  });

  youtubeIframeApiPromise = pendingPromise;
  void pendingPromise.catch(() => {
    if (youtubeIframeApiPromise === pendingPromise) {
      youtubeIframeApiPromise = null;
    }
  });

  return pendingPromise;
};

const externalYoutubeUrl = (videoId: string, playbackStartMilliseconds: number) =>
  `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}&t=${Math.max(0, Math.floor(playbackStartMilliseconds / 1000))}s`;

const findActiveCue = (
  captions: YoutubeCaption[],
  currentMilliseconds: number,
  preferredSentenceId: number | undefined
): ActiveCue | undefined => {
  const isActive = (cue: { startMilliseconds: number; endMilliseconds: number }) =>
    cue.startMilliseconds <= currentMilliseconds &&
    currentMilliseconds < cue.endMilliseconds;

  const toActiveCue = (caption: YoutubeCaption, cue: YoutubeCaption['cues'][number]): ActiveCue => ({
    sentenceId: caption.sentenceId,
    text: cue.text,
    startChar: cue.startChar,
    startMilliseconds: cue.startMilliseconds,
    endMilliseconds: cue.endMilliseconds,
  });

  const preferredCaption = captions.find((caption) => caption.sentenceId === preferredSentenceId);
  const preferredCue = preferredCaption?.cues.find(isActive);
  if (preferredCaption && preferredCue) {
    return toActiveCue(preferredCaption, preferredCue);
  }

  for (const caption of captions) {
    const cue = caption.cues.find(isActive);
    if (cue) {
      return toActiveCue(caption, cue);
    }
  }

  return undefined;
};

const playerErrorMessage = (code: number) => {
  if (code === 2) {
    return 'YouTube rejected the video address.';
  }
  if (code === 5) {
    return 'This video cannot be played in this browser.';
  }
  if (code === 100) {
    return 'This video is private or no longer available.';
  }
  if (code === 101 || code === 150) {
    return 'This video cannot be played in an embedded player.';
  }
  if (code === 153) {
    return 'YouTube could not verify this page. Open the video on YouTube instead.';
  }
  return 'The embedded video could not be loaded.';
};

export interface SynchronizedYoutubePlayerHandle {
  replay: () => void;
  /** Jumps relative to the current position, clamped at the start of the video. */
  seekBy: (seconds: number) => void;
  togglePlay: () => void;
}

export interface SynchronizedYoutubePlayerProps {
  videoId: string;
  playbackStartMilliseconds: number;
  sentenceId: number | undefined;
  highlightSpans: YoutubeHighlightSpan[];
  captions: YoutubeCaption[];
  captionsLoading: boolean;
  captionsError: string;
  onStatusChange: (status: { loading: boolean; error: boolean }) => void;
  /** Reports whether the video is currently running, e.g. to drive a play/pause button. */
  onPlayingChange?: (playing: boolean) => void;
  /** Absolutely positioned content layered over the video frame, e.g. a status badge. */
  overlay?: ReactNode;
  /** Rendered between the video and the subtitle box, e.g. the video's title. */
  details?: ReactNode;
  /** Extra classes on the video frame only, e.g. a negative margin to bleed past a padded parent. */
  frameClassName?: string;
  /** Extra classes on the subtitle box only, e.g. to bleed it past a padded parent. */
  captionClassName?: string;
}

export const SynchronizedYoutubePlayer = forwardRef<
  SynchronizedYoutubePlayerHandle,
  SynchronizedYoutubePlayerProps
>(function SynchronizedYoutubePlayer(
  { videoId, playbackStartMilliseconds, sentenceId, highlightSpans, captions, captionsLoading, captionsError, onStatusChange, onPlayingChange, overlay, details, frameClassName, captionClassName },
  ref
) {
  const playerHostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YoutubePlayer | null>(null);
  const loadedVideoIdRef = useRef('');
  const playbackStartRef = useRef(playbackStartMilliseconds);
  const [currentMilliseconds, setCurrentMilliseconds] = useState(playbackStartMilliseconds);
  const [playerError, setPlayerError] = useState('');
  const [playerLoading, setPlayerLoading] = useState(true);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [playing, setPlaying] = useState(false);

  playbackStartRef.current = playbackStartMilliseconds;

  useEffect(() => {
    let disposed = false;
    let player: YoutubePlayer | null = null;
    let readyTimeoutId: number | undefined;
    const host = playerHostRef.current;
    if (!host) return;

    setPlayerLoading(true);
    setPlayerError('');
    setAutoplayBlocked(false);
    setPlaying(false);
    host.replaceChildren();

    loadYoutubeIframeApi()
      .then((api) => {
        if (disposed) return;

        const startMilliseconds = playbackStartRef.current;
        const iframe = document.createElement('iframe');
        const parameters = new URLSearchParams({
          // loadVideoById starts playback after the API is ready and preserves sub-second timing.
          autoplay: '0',
          // Subtitles are rendered below the video; YouTube's own would duplicate them.
          cc_load_policy: '0',
          // The match is navigated with our own Replay/Previous/Next buttons, so YouTube's
          // scrubber and control bar would only compete with them.
          controls: '0',
          enablejsapi: '1',
          // No annotation or card overlays on top of the sentence being studied.
          iv_load_policy: '3',
          // Deprecated by YouTube and mostly a no-op now; kept for older embeds.
          modestbranding: '1',
          origin: window.location.origin,
          playsinline: '1',
          rel: '0',
          start: String(Math.max(0, Math.floor(startMilliseconds / 1000))),
        });
        iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${parameters}`;
        iframe.title = `YouTube video ${videoId}`;
        iframe.className = 'size-full border-0';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        host.replaceChildren(iframe);

        readyTimeoutId = window.setTimeout(() => {
          if (disposed) return;
          console.warn('YouTube player readiness timed out.', { videoId });
          setPlayerError('The embedded video player took too long to respond.');
          setPlayerLoading(false);
        }, PLAYER_READY_TIMEOUT_MILLISECONDS);

        player = new api.Player(iframe, {
          events: {
            onReady: (event) => {
              if (disposed) return;
              window.clearTimeout(readyTimeoutId);
              playerRef.current = event.target;
              const readyStart = playbackStartRef.current;
              setPlayerError('');
              setAutoplayBlocked(false);
              try {
                event.target.loadVideoById({
                  videoId,
                  startSeconds: Math.max(0, readyStart / 1000),
                });
                hideYoutubeCaptions(event.target);
                loadedVideoIdRef.current = videoId;
                setCurrentMilliseconds(readyStart);
                setPlayerLoading(false);
              } catch (error) {
                console.error('Starting YouTube playback failed.', error);
                setPlayerError('Unable to start the embedded video.');
                setPlayerLoading(false);
              }
            },
            onAutoplayBlocked: () => {
              if (disposed) return;
              setAutoplayBlocked(true);
              setPlayerLoading(false);
            },
            onStateChange: (event) => {
              if (disposed) return;
              const isRunning =
                event.data === PLAYER_STATE_PLAYING || event.data === PLAYER_STATE_BUFFERING;
              if (isRunning) {
                hideYoutubeCaptions(playerRef.current ?? player!);
              }
              setPlaying(isRunning);
            },
            onError: (event) => {
              if (disposed) return;
              window.clearTimeout(readyTimeoutId);
              console.warn('YouTube player reported an error.', { videoId, code: event.data });
              setAutoplayBlocked(false);
              setPlayerError(playerErrorMessage(event.data));
              setPlayerLoading(false);
            },
          },
        });
      })
      .catch((error) => {
        if (!disposed) {
          window.clearTimeout(readyTimeoutId);
          console.error('YouTube player initialization failed.', error);
          setPlayerError('Unable to initialize the embedded video player.');
          setPlayerLoading(false);
        }
      });

    return () => {
      disposed = true;
      window.clearTimeout(readyTimeoutId);
      playerRef.current = null;
      player?.destroy();
      host.replaceChildren();
    };
  }, [videoId]);

  /**
   * Moving inside the video that is already loaded is a seek, not a reload. YouTube
   * flashes its own chrome — title bar, play indicator, related-video strip — over the
   * frame on every load, and only a seek keeps the picture clean.
   */
  const startPlayback = (startMilliseconds: number) => {
    const player = playerRef.current;
    if (!player) return;

    const startSeconds = Math.max(0, startMilliseconds / 1000);
    try {
      if (loadedVideoIdRef.current === videoId) {
        player.seekTo(startSeconds, true);
        player.playVideo();
        return;
      }

      player.loadVideoById({ videoId, startSeconds });
      loadedVideoIdRef.current = videoId;
    } catch (error) {
      console.error('Starting YouTube playback failed.', error);
      setPlayerError('Unable to start the embedded video.');
    }
  };

  useEffect(() => {
    setCurrentMilliseconds(playbackStartMilliseconds);
    setPlayerError('');
    setAutoplayBlocked(false);
    startPlayback(playbackStartMilliseconds);
    // startPlayback is rebuilt every render and reads the props above; listing it here
    // would restart playback on unrelated renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, playbackStartMilliseconds]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const seconds = playerRef.current?.getCurrentTime();
      if (seconds !== undefined && Number.isFinite(seconds)) {
        setCurrentMilliseconds(Math.max(0, seconds * 1000));
      }
    }, PLAYER_POLL_INTERVAL_MILLISECONDS);

    return () => window.clearInterval(intervalId);
  }, []);

  const activeCue = findActiveCue(captions, currentMilliseconds, sentenceId);
  const highlights = activeCue && activeCue.sentenceId === sentenceId
    ? (highlightSpans ?? [])
        .map((span) => ({
          start: Math.max(0, span.start - activeCue.startChar),
          end: Math.min(activeCue.text.length, span.end - activeCue.startChar),
        }))
        .filter((span) => span.end > span.start)
        .map((span) => ({ start: span.start, length: span.end - span.start }))
    : [];

  const replayMatch = () => {
    setCurrentMilliseconds(playbackStartMilliseconds);
    setPlayerError('');
    setAutoplayBlocked(false);
    startPlayback(playbackStartMilliseconds);
  };

  const seekBy = (seconds: number) => {
    const player = playerRef.current;
    if (!player) return;

    try {
      const currentSeconds = player.getCurrentTime();
      const targetSeconds = Math.max(0, (Number.isFinite(currentSeconds) ? currentSeconds : currentMilliseconds / 1000) + seconds);
      player.seekTo(targetSeconds, true);
      // The poll only runs every quarter second, so move the subtitle straight away.
      setCurrentMilliseconds(targetSeconds * 1000);
    } catch (error) {
      console.error('Seeking the embedded video failed.', error);
    }
  };

  const togglePlay = () => {
    const player = playerRef.current;
    if (!player) return;

    try {
      if (playing) {
        player.pauseVideo();
      } else {
        setAutoplayBlocked(false);
        player.playVideo();
      }
    } catch (error) {
      console.error('Toggling the embedded video failed.', error);
    }
  };

  const playAfterAutoplayBlock = () => {
    if (!playerRef.current) {
      setPlayerError('The embedded video player is not ready.');
      return;
    }

    setAutoplayBlocked(false);
    startPlayback(playbackStartMilliseconds);
  };

  useImperativeHandle(ref, () => ({ replay: replayMatch, seekBy, togglePlay }));

  useEffect(() => {
    onStatusChange({ loading: playerLoading, error: Boolean(playerError) });
  }, [onStatusChange, playerLoading, playerError]);

  useEffect(() => {
    onPlayingChange?.(playing);
  }, [onPlayingChange, playing]);

  return (
    <div className="space-y-3">
      <div className={cn('relative aspect-video overflow-hidden bg-black shadow-inner', frameClassName)}>
        <div ref={playerHostRef} className="size-full" />
        {playerLoading && !playerError && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-slate-950 text-sm text-white">
            <LoaderCircle className="size-5 animate-spin" />
            Loading player...
          </div>
        )}
        {autoplayBlocked && !playerError && (
          <div role="status" className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950/90 px-6 text-center text-white">
            <p className="max-w-md text-sm leading-6">Your browser blocked automatic playback.</p>
            <Button type="button" onClick={playAfterAutoplayBlock} className="rounded-full bg-red-600 text-white hover:bg-red-700">
              <Play className="size-4 fill-current" />
              Play video
            </Button>
          </div>
        )}
        {playerError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950 px-6 text-center text-white">
            <VideoOff className="size-9 text-red-300" />
            <p className="max-w-md text-sm leading-6">{playerError}</p>
            <Button asChild variant="outline" className="rounded-full border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white">
              <a href={externalYoutubeUrl(videoId, playbackStartMilliseconds)} target="_blank" rel="noopener noreferrer">
                Open on YouTube
                <ExternalLink className="size-4" />
              </a>
            </Button>
          </div>
        )}
        {overlay}
      </div>

      {details}

      <div
        className={cn(
          'flex min-h-24 items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 px-5 py-4 text-center',
          captionClassName
        )}
      >
        {captionsLoading ? (
          <span className="inline-flex items-center gap-2 text-sm text-slate-500">
            <LoaderCircle className="size-4 animate-spin" />
            Loading subtitles...
          </span>
        ) : captionsError ? (
          <p role="alert" className="text-sm text-red-700">{captionsError}</p>
        ) : (
          <AnimatePresence mode="wait">
            {activeCue ? (
              <motion.p
                key={`${activeCue.sentenceId}-${activeCue.startMilliseconds}`}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                className="text-xl font-medium leading-9 text-slate-800"
              >
                <HighlightedSelections text={activeCue.text} selections={highlights} />
              </motion.p>
            ) : (
              <motion.span
                key="empty"
                aria-hidden="true"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                &nbsp;
              </motion.span>
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
});
